import {
  CLIENT_STAGE_LABELS,
  PROJECT_STATUS_LABELS,
  type ActivityDto,
  type ClientStage,
  type ProjectStatus,
} from '@cw/shared';

const KIND: Record<string, string> = {
  logo: 'um logo',
  logo_variation: 'uma variação de logo',
  color: 'uma cor',
  font: 'uma fonte',
  element: 'um elemento',
  texture: 'uma textura',
  template: 'um template',
  brandbook: 'o brandbook',
  voice: 'o tom de voz',
  link: 'um link',
};

/** Frase da timeline: "Luca enviou o arquivo KV.png". O nome do autor fica fora, em destaque. */
export function activityText(a: ActivityDto): string {
  const d = a.data as Record<string, unknown>;
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  switch (a.action) {
    case 'client.created':
      return 'cadastrou o cliente';
    case 'client.updated':
      return 'atualizou o cadastro';
    case 'client.status_changed':
      return `mudou o status de ${s('from')} para ${s('to')}`;
    case 'client.logo_changed':
      return 'trocou o logo';
    case 'contact.created':
      return `adicionou o contato ${s('name')}`;
    case 'contact.deleted':
      return `removeu o contato ${s('name')}`;
    case 'brand_asset.created':
      return `adicionou ${KIND[s('kind')] ?? 'um asset'} ao Brand Hub: ${s('name')}`;
    case 'brand_asset.deleted':
      return `removeu ${KIND[s('kind')] ?? 'um asset'} do Brand Hub: ${s('name')}`;
    case 'file.uploaded':
      return `enviou o arquivo ${s('name')}`;
    case 'file.updated':
      return 'atualizou um arquivo';
    case 'file.deleted':
      return `removeu o arquivo ${s('name')}`;
    case 'client_user.invited':
      return `liberou acesso para ${s('email')}`;
    case 'client_user.removed':
      return `removeu o acesso de ${s('name')}`;
    case 'project.created':
      return a.entityType === 'project' && s('name') ? `criou o projeto ${s('name')}` : 'criou o projeto';
    case 'project.updated':
      return 'atualizou o projeto';
    case 'project.status_changed':
      return `moveu o projeto para ${PROJECT_STATUS_LABELS[s('to') as ProjectStatus] ?? s('to')}`;
    case 'project.briefing_updated':
      return 'editou o briefing';
    case 'project.description_updated':
      return 'editou a descrição';
    case 'project.member_added':
      return 'adicionou alguém à equipe do projeto';
    case 'project.member_removed':
      return 'removeu alguém da equipe do projeto';
    case 'project.deleted':
      return `excluiu o projeto ${s('name')}`;
    case 'task.created':
      return `criou a tarefa ${s('title')}`;
    case 'subtask.created':
      return `criou a subtarefa ${s('title')}`;
    case 'task.assigned':
      return `atribuiu ${s('title')}`;
    case 'task.status_changed': {
      // Mudou de etapa do quadro: o nome da etapa (o mesmo que o cliente vê), não o passo interno.
      const stage = s('toStage') && s('toStage') !== s('fromStage') ? s('toStage') : '';
      const to = stage ? CLIENT_STAGE_LABELS[stage as ClientStage] : s('to');
      return `moveu ${s('title')}${to ? ` para ${to}` : ''}`;
    }
    case 'task.paused':
    case 'project.paused':
      return `pausou ${s('title') || s('name')}`;
    case 'task.resumed':
    case 'project.resumed':
      return `retomou ${s('title') || s('name')}`;
    case 'delivery.approved':
      return `aprovou ${s('title')}${s('comment') ? `: “${s('comment')}”` : ''}`;
    case 'delivery.changes_requested':
      return `pediu ajuste em ${s('title')}: “${s('comment')}”`;
    case 'delivery.rejected':
      return `recusou ${s('title')}: “${s('comment')}”`;
    case 'task.due_changed':
      return `mudou o prazo de ${s('title')}`;
    case 'task.deleted':
      return `excluiu a tarefa ${s('title')}`;
    case 'comment.created':
      return `comentou em ${s('label')}${s('excerpt') ? `: “${s('excerpt')}”` : ''}`;
    case 'request.created':
      return `enviou a solicitação ${s('title')}`;
    case 'request.accepted':
      return `aceitou a solicitação ${s('title')}`;
    case 'event.created':
      return `marcou ${s('title')}`;
    case 'event.updated':
    case 'event.moved':
      return `remarcou ${s('title')}`;
    case 'event.deleted':
      return `cancelou ${s('title')}`;
    case 'file.version_restored':
      return `voltou ${s('name')} para uma versão anterior`;
    case 'approval.requested':
      return `pediu aprovação de ${s('name')} V${String(d.version ?? '')}`;
    case 'approval.approved':
      return `aprovou ${s('name')}${s('comment') ? `: “${s('comment')}”` : ''}`;
    case 'approval.changes_requested':
      return `pediu alteração em ${s('name')}: “${s('comment')}”`;
    case 'approval.rejected':
      return `rejeitou ${s('name')}: “${s('comment')}”`;
    case 'board.created':
      return `criou o board ${s('title')}`;
    case 'moodboard.created':
      return `criou o moodboard ${s('title')}`;
    case 'board.renamed':
    case 'moodboard.renamed':
      return `renomeou o board para ${s('to')}`;
    case 'board.deleted':
    case 'moodboard.deleted':
      return `excluiu ${s('title')}`;
    case 'storyboard.created':
      return `criou o storyboard ${s('title')}`;
    case 'storyboard.version_saved':
      return `salvou a V${String(d.version ?? '')} de ${s('title')}`;
    case 'storyboard.version_restored':
      return `restaurou a V${String(d.version ?? '')} de ${s('title')}`;
    case 'storyboard.status_changed':
      return `mudou o status de ${s('title')}`;
    case 'storyboard.deleted':
      return `excluiu o storyboard ${s('title')}`;
    case 'note.created':
      return `criou a nota ${s('title') || 'sem título'}`;
    case 'note.deleted':
      return `excluiu a nota ${s('title')}`;
    case 'share.created':
      return 'criou um link de apresentação';
    case 'share.viewed':
      return `abriu a apresentação${s('title') ? ` ${s('title')}` : ''}`;
    case 'contract.created':
      return `cadastrou o contrato ${s('title')}`;
    case 'invoice.created':
      return `criou a fatura #${String(d.number ?? '').padStart(4, '0')}`;
    case 'invoice.issued':
      return `emitiu a fatura #${String(d.number ?? '').padStart(4, '0')}`;
    case 'invoice.cancelled':
      return `cancelou a fatura #${String(d.number ?? '').padStart(4, '0')}`;
    case 'payment.received':
      return `registrou pagamento na fatura #${String(d.number ?? '').padStart(4, '0')}`;
    case 'project.template_applied':
      return `aplicou o template ${s('template')}`;
    default:
      return a.action;
  }
}

export function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `há ${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}
